import React, { useMemo } from 'react'
import { graphql, useStaticQuery } from 'gatsby'
import { Block, Content, Yoga } from 'gerami'
import {
  FaFacebook,
  FaInstagram,
  FaLocationArrow,
  FaTelegram,
  FaTwitter,
  FaYoutube
} from 'react-icons/fa'

import './footer.scss'
import { FooterQuery } from '../../../../../../graphql-types'
import { NavCategory, navData } from '../nav/nav-data'
import useSiteMetadata from '../../../../hooks/use-site-metadata/use-site-metadata'
import Anchor from '../../../anchor/anchor'

type FooterProps = {}

const Footer: React.FC<FooterProps> = ({}) => {
  const data = useStaticQuery<FooterQuery>(query)

  const navs = useMemo(
    () => ({
      primaries: navData.filter(nav => nav.category === NavCategory.PRIMARY),
      foreigners: navData.filter(nav => nav.category === NavCategory.FOREIGN),
      secondaries: navData.filter(nav => nav.category === NavCategory.SECONDARY)
    }),
    []
  )

  const { social, address, addressUrl, mail, phones, emails } =
    data.generalInfo?.frontmatter ?? {}

  const siteMetadata = useSiteMetadata()

  return (
    <footer className="footer fg-whitish">
      <Content transparent size="4XL">
        <Block className="padding-vertical-none">
          <Yoga maxCol={2}>
            <div className="margin-top-very-big">
              <h4>Links and Social</h4>

              {!navs.primaries.length ? null : (
                <div className="padding-top-big">
                  {navs.primaries.map((nav, i) => (
                    <span key={i}>
                      <Anchor to={nav.url}>{nav.name}</Anchor>
                      {i >= navs.primaries.length - 1 ? null : (
                        <span className="inline-block padding-horizontal-normal">
                          •
                        </span>
                      )}
                    </span>
                  ))}
                </div>
              )}

              {!navs.foreigners.length ? null : (
                <div className="padding-top-big">
                  {navs.foreigners.map((nav, i) => (
                    <span key={i}>
                      <Anchor
                        to={nav.url}
                        target="_blank"
                        rel="noopener nofollow"
                      >
                        {nav.name}
                      </Anchor>
                      {i >= navs.foreigners.length - 1 ? null : (
                        <span className="inline-block padding-horizontal-normal">
                          •
                        </span>
                      )}
                    </span>
                  ))}
                </div>
              )}

              {!navs.secondaries.length ? null : (
                <div className="padding-top-big">
                  {navs.secondaries.map((nav, i) => (
                    <span key={i}>
                      <Anchor to={nav.url}>{nav.name}</Anchor>
                      {i >= navs.secondaries.length - 1 ? null : (
                        <span className="inline-block padding-horizontal-normal">
                          •
                        </span>
                      )}
                    </span>
                  ))}
                </div>
              )}

              <div className="padding-top-big">
                {!social!.facebook ? null : (
                  <span>
                    <Anchor
                      to={social!.facebook}
                      title="Facebook"
                      className="footer-social"
                      target="_blank"
                      rel="noopener nofollow"
                    >
                      <FaFacebook />
                    </Anchor>
                  </span>
                )}
                {!social!.instagram ? null : (
                  <span>
                    <Anchor
                      to={social!.instagram}
                      title="Instagram"
                      className="footer-social"
                      target="_blank"
                      rel="noopener nofollow"
                    >
                      <FaInstagram />
                    </Anchor>
                  </span>
                )}
                {!social!.telegram ? null : (
                  <span>
                    <Anchor
                      to={social!.telegram}
                      title="Telegram"
                      className="footer-social"
                      target="_blank"
                      rel="noopener nofollow"
                    >
                      <FaTelegram />
                    </Anchor>
                  </span>
                )}
                {!social!.twitter ? null : (
                  <span>
                    <Anchor
                      to={social!.twitter}
                      title="Twitter"
                      className="footer-social"
                      target="_blank"
                      rel="noopener nofollow"
                    >
                      <FaTwitter />
                    </Anchor>
                  </span>
                )}
                {!social!.youtube ? null : (
                  <span>
                    <Anchor
                      to={social!.youtube}
                      title="YouTube"
                      className="footer-social"
                      target="_blank"
                      rel="noopener nofollow"
                    >
                      <FaYoutube />
                    </Anchor>
                  </span>
                )}
              </div>
            </div>

            <div className="margin-top-very-big">
              <h4>Address and Contact</h4>

              <Block className="padding-none">
                <Yoga maxCol={2}>
                  <div>
                    <div>
                      <div className="bold">Address</div>
                      <div className="padding-top-small">
                        <pre>{address!}</pre>
                        <div>
                          <Anchor
                            to={addressUrl!}
                            target="_blank"
                            rel="noopener nofollow"
                          >
                            Get Directions
                            <small className="inline-block margin-left-normal">
                              <small>
                                <FaLocationArrow />
                              </small>
                            </small>
                          </Anchor>
                        </div>
                      </div>
                    </div>

                    <div className="padding-top-big">
                      <div className="bold">Mail</div>
                      <div className="padding-top-small">
                        <pre>{mail!}</pre>
                      </div>
                    </div>
                  </div>

                  <div>
                    <div>
                      <div className="bold">
                        Phone{phones!.length === 1 ? `` : `s`}
                      </div>
                      <div className="padding-top-small">
                        {phones!.map((phone, i) => (
                          <div key={i}>
                            <Anchor
                              to={`tel:${phone!
                                .replace(/ /g, ``)
                                .replace(/-/g, '')}`}
                              target="_blank"
                              rel="noopener nofollow"
                            >
                              {phone!}
                            </Anchor>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="padding-top-big">
                      <div className="bold">
                        Email{emails!.length === 1 ? `` : `s`}
                      </div>
                      <div className="padding-top-small">
                        {emails!.map((email, i) => (
                          <div key={i}>
                            <Anchor
                              to={`mailto:${email!}`}
                              target="_blank"
                              rel="noopener nofollow"
                            >
                              {email!}
                            </Anchor>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </Yoga>
              </Block>
            </div>
          </Yoga>
        </Block>

        <Block>
          <hr style={{ opacity: 0.07 }} />
        </Block>

        <Block last>
          {siteMetadata.copyright}
          <span className="inline-block padding-horizontal-normal">•</span>
          Powered by{' '}
          <Anchor to="https://www.kelaltech.com" target="_blank" rel="noopener">
            kelal tech.
          </Anchor>
        </Block>
      </Content>
    </footer>
  )
}

export default Footer

const query = graphql`
  query Footer {
    generalInfo: markdownRemark(fields: { slug: { regex: "/info/general/" } }) {
      frontmatter {
        social {
          facebook
          instagram
          telegram
          twitter
          youtube
        }
        address
        addressUrl
        mail
        phones
        emails
      }
    }
  }
`
